/**
 * Pure component for choosing a password as part of the new user flow.
 */
import React, { FunctionComponent, useEffect, useState } from 'react';
import classNames from 'classnames';
import NewUserButton from './common/new-user-button';
import NewUserPasswordInput from './common/new-user-password-input';

// Min length of password
const MIN_PASSWORD_LENGTH = 6;

interface IChoosePasswordView {
    onSubmit: (password: string) => void; // Called on submit
    onBack: (password: string) => void; // Called on back button press
    chosenPassword?: string; // Optional prop, if user has set password already input will be populated
}

/**
 * View for allowing users to choose their own password. Only shows in the V2 flow.
 */
const ChoosePasswordView: FunctionComponent<IChoosePasswordView> = ({
    onSubmit,
    onBack,
    chosenPassword,
}: IChoosePasswordView) => {
    const [password, setPassword] = useState<string>('');

    useEffect(
        () => {
            if (chosenPassword) {
                setPassword(chosenPassword);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const passwordValid: boolean = password.length >= MIN_PASSWORD_LENGTH;
    const passwordStrengthField = (
        <div className="new-user-password-strength">
            <p>Your password needs to:</p>
            <p>
                <span
                    className={classNames('user-user-dot', {
                        valid: passwordValid,
                    })}
                >
                    <i className="fa fa-check-circle" />
                </span>{' '}
                Be {MIN_PASSWORD_LENGTH} or more characters
            </p>
        </div>
    );

    return (
        <div className="new-user-choose-password-page">
            <div className="new-user-view-content">
                <div className="new-user-view-box choose-password">
                    <p className="new-user-username-reveal-header">
                        Now set a password, make sure you choose one that you will remember
                    </p>

                    <form
                        onSubmit={evt => {
                            evt.preventDefault();

                            if (passwordValid) {
                                onSubmit(password);
                            }
                        }}
                    >
                        <NewUserPasswordInput
                            password={password}
                            handleChange={setPassword}
                            label="Choose your password"
                        />

                        {passwordStrengthField}

                        <div className="new-user-view-form-button-group new-user-choose-password-btn-group">
                            <NewUserButton
                                type="button"
                                value="Back"
                                handleClick={() => onBack(password)}
                            />

                            <NewUserButton
                                type="submit"
                                value="Confirm your details"
                                disabled={!passwordValid}
                                handleClick={() => onSubmit(password)}
                                showIcon={true}
                            />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ChoosePasswordView;
